import { useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: benefit-plan-tabs subdirectory requires four levels up to reach convex/_generated/
import { EditableJsonTable } from './EditableJsonTable';
import type {
  IBenefitPlan,
  IBenefitSection,
  IBenefitItem,
  IGeneralExclusion,
} from '@/@types/claims';
import type { IEditableJsonTableColumn } from '@/@types/insurance';

interface IGeneralExclusionsTabProps {
  // LOCAL_TYPE_JUSTIFIED: component-specific props combining plan with enriched sections
  plan: IBenefitPlan & {
    sections: Array<IBenefitSection & { items: IBenefitItem[] }>;
  };
}

const EXCLUSION_COLORS: Record<string, 'green' | 'blue' | 'orange' | 'red' | 'violet'> = {
  'Pre-existing exclusion': 'red',
  'Pre-existing conditions': 'red',
  Sanctions: 'orange',
  'Waiting period — maternity': 'violet',
  'Waiting period — pre-existing': 'orange',
  'Cosmetic treatment': 'orange',
  'Experimental treatment': 'violet',
  'Self-inflicted injury': 'red',
  'War / civil unrest': 'red',
  'Nuclear / radiation': 'red',
  'Criminal act': 'red',
  'Substance abuse': 'orange',
  'HIV/AIDS exclusion': 'red',
  'Fertility / IVF': 'violet',
  'Chronic condition exclusion': 'orange',
  'Dental / routine': 'blue',
  'Optical / routine': 'blue',
  'Hearing aids': 'blue',
  'Late submission': 'red',
};

const COLUMNS: IEditableJsonTableColumn<IGeneralExclusion>[] = [
  { key: 'id', header: 'ID', width: '50px', readOnly: true },
  {
    key: 'exclusionCategory',
    header: 'Exclusion Category',
    width: '220px',
    badgeColorMap: EXCLUSION_COLORS,
  },
  { key: 'exclusionDetail', header: 'Exclusion Detail' },
];

export function GeneralExclusionsTab({ plan }: IGeneralExclusionsTabProps) {
  const updateTabData = useMutation(api.insurance.benefitPlans.updateTabData);

  const rows: IGeneralExclusion[] = (plan.generalExclusions ?? []).map((r, i) => ({
    ...r,
    id: r.id || `E${String(i + 1).padStart(3, '0')}`,
  }));

  async function handleSave(updated: IGeneralExclusion[]) {
    await updateTabData({ planId: plan._id, field: 'generalExclusions', data: updated });
  }

  return (
    <div className="py-6 px-8">
      <EditableJsonTable
        rows={rows}
        columns={COLUMNS}
        idPrefix="E"
        onSave={handleSave}
        emptyMessage="No general exclusions defined."
      />
    </div>
  );
}
